import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatDate, cn } from "@/lib/utils";
import {
  ShieldCheck,
  ShieldAlert,
  ArrowLeft,
  Clock,
  CheckCircle,
  Check,
  XCircle,
  FileText,
  Printer,
  PenTool,
  Truck,
  PackageCheck,
  AlertTriangle,
  Receipt,
  User,
  Tag,
  Loader2,
  Calendar,
  AlertCircle,
  Trash2,
  Pencil,
  FilePlus,
  RotateCcw,
} from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import warrantyApi from "@/api/warrantyApi";
import { all_routes } from "@/Router/all_routes";
import {
  getClaimStatusValue,
  getClaimStatusLabel,
  canTransition,
  getDocumentType,
} from "@/lib/warrantyUtils";

import AssessClaimModal from "./components/AssessClaimModal";
import ApproveClaimModal from "./components/ApproveClaimModal";
import FulfilReplacementModal from "./components/FulfilReplacementModal";
import RejectClaimModal from "./components/RejectClaimModal";
import ResolveClaimModal from "./components/ResolveClaimModal";
import SupplierRmaModal from "./components/SupplierRmaModal";
import SignatureCaptureModal from "./components/SignatureCaptureModal";
import WarrantyDocumentModal from "./components/WarrantyDocumentModal";
import EditWarrantyClaimModal from "./components/EditWarrantyClaimModal";

const statusVariants = {
  pending: "warning",
  under_assessment: "secondary",
  approved: "info",
  in_progress: "secondary",
  resolved: "success",
  closed: "outline",
  rejected: "destructive",
};

const WarrantyClaimDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const isNewClaimBanner = location.state?.isNewClaim;

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const currentStore = useSelector((state) => state.current_store);
  const hasPermission = (perm) => permissions.includes(perm);

  // Modals state
  const [assessModalOpen, setAssessModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [fulfilModalOpen, setFulfilModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [selectedDocToSign, setSelectedDocToSign] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Document action modal (Download & Print choice)
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [selectedDocForAction, setSelectedDocForAction] = useState({
    title: "Warranty Document",
    url: "",
    filename: "",
  });

  // Confirm dialogs
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isWarningDismissed, setIsWarningDismissed] = useState(false);
  const [confirmRemoveDoc, setConfirmRemoveDoc] = useState(null);
  const [removingDocId, setRemovingDocId] = useState(null);

  // Fetch claim details
  const {
    data: claimResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["warrantyClaim", id],
    queryFn: async () => {
      const res = await warrantyApi.show(id, {
        with: [
          "customer",
          "product",
          "store",
          "handledBy",
          "documents",
          "supplier",
          "sale",
          "saleItem",
        ],
      });
      return res.data;
    },
    enabled: Boolean(id) && hasPermission("warranty claim read"),
  });

  const claim = claimResponse?.data || claimResponse;

  // Mutations
  const closeMutation = useMutation({
    mutationFn: () => warrantyApi.close(id),
    onSuccess: () => {
      toast.success("Claim closed successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      refetch();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to close claim");
      refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => warrantyApi.rejectViaDelete(id),
    onSuccess: () => {
      toast.success("Claim rejected / deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      navigate(all_routes.warrantyClaims);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to reject claim");
      refetch();
    },
  });

  const issueDocMutation = useMutation({
    mutationFn: (type) => warrantyApi.issueDocument(id, type),
    onSuccess: (_, type) => {
      toast.success(`${type.toUpperCase()} document issued successfully`);
      refetch();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to issue document");
      refetch();
    },
  });

  const removeSigMutation = useMutation({
    mutationFn: async ({ documentId }) => {
      setRemovingDocId(documentId);
      return await warrantyApi.removeSignature(claim.id, documentId);
    },
    onSuccess: () => {
      toast.success(
        "Signature removed successfully. Document is ready for physical signing.",
      );
      refetch();
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || err.message || "Failed to remove signature",
      );
      refetch();
    },
    onSettled: () => {
      setRemovingDocId(null);
      setConfirmRemoveDoc(null);
    },
  });

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center min-h-[350px] gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading claim details...</p>
        </div>
      </PageContainer>
    );
  }

  if (isError || !claim) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center min-h-[350px] gap-4">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <div className="text-center">
            <h3 className="font-semibold text-lg">Claim Not Found</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {error?.response?.data?.message || "The requested warranty claim could not be loaded."}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate(all_routes.warrantyClaims)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Warranty Claims
          </Button>
        </div>
      </PageContainer>
    );
  }

  const statusVal = getClaimStatusValue(claim);
  const statusLabel = getClaimStatusLabel(claim);

  const replacementStatusVal =
    typeof claim.replacement_status === "object" && claim.replacement_status !== null
      ? claim.replacement_status.value
      : claim.replacement_status;

  const returnConditionLabel =
    typeof claim.return_condition === "object" && claim.return_condition !== null
      ? claim.return_condition.label || claim.return_condition.value
      : claim.return_condition || "Defective";

  const resolutionTypeVal =
    typeof claim.resolution_type === "object" && claim.resolution_type !== null
      ? claim.resolution_type.value
      : claim.resolution_type;

  const resolutionTypeLabel =
    typeof claim.resolution_type === "object" && claim.resolution_type !== null
      ? claim.resolution_type.label || claim.resolution_type.value
      : claim.resolution_type;

  const rejectionReasonLabel =
    typeof claim.rejection_reason === "object" && claim.rejection_reason !== null
      ? claim.rejection_reason.label || claim.rejection_reason.value
      : claim.rejection_reason;

  const supplierClaimStatusVal =
    typeof claim.supplier_claim_status === "object" && claim.supplier_claim_status !== null
      ? claim.supplier_claim_status.value
      : claim.supplier_claim_status;

  const supplierClaimStatusLabel =
    typeof claim.supplier_claim_status === "object" && claim.supplier_claim_status !== null
      ? claim.supplier_claim_status.label || claim.supplier_claim_status.value
      : claim.supplier_claim_status;

  const currencySymbol =
    claim.store?.default_currency?.symbol ||
    currentStore?.default_currency?.symbol ||
    "$";

  const assessedByName =
    claim.assessed_by_user?.employee?.full_name ||
    claim.assessed_by_user?.username ||
    claim.assessedBy?.name ||
    claim.assessedBy?.full_name;

  const isAwaitingStock = replacementStatusVal === "pending";
  const isTerminal = ["closed", "rejected"].includes(statusVal);

  // Rule: drive buttons strictly from allowed_transitions + permissions
  const canAssess =
    hasPermission("warranty claim assess") &&
    canTransition(claim, "under_assessment", "assess");

  const canApprove =
    hasPermission("warranty claim approve") &&
    canTransition(claim, "approved", "approve");

  const canFulfil =
    hasPermission("warranty claim fulfil") &&
    (isAwaitingStock || canTransition(claim, "fulfil", "fulfilled"));

  const canResolve =
    (hasPermission("warranty claim update_status") ||
      hasPermission("warranty claim approve")) &&
    canTransition(claim, "resolved", "resolve");

  const canClose =
    (hasPermission("warranty claim update_status") ||
      hasPermission("warranty claim close")) &&
    canTransition(claim, "closed", "close");

  const canReject =
    hasPermission("warranty claim reject") &&
    canTransition(claim, "rejected", "reject");

  const canEditClaim =
    hasPermission("warranty claim update") && !isTerminal;

  const canSupplierClaim =
    hasPermission("warranty claim supplier_claim") && !isTerminal;

  const canEscapeDelete =
    (hasPermission("warranty claim delete") || hasPermission("warranty claim reject")) &&
    statusVal !== "closed";

  const isNoWarranty =
    claim.product?.warranty === 0 ||
    claim.product?.warranty_timeframe === "No Warranty" ||
    (claim.warranty_start_date &&
      claim.warranty_end_date &&
      claim.warranty_start_date === claim.warranty_end_date &&
      !claim.is_warranty_valid);

  const hasSupplierRma = Boolean(
    claim.supplier_claim_reference ||
    claim.supplier_id ||
    claim.supplier ||
    (supplierClaimStatusVal && supplierClaimStatusVal !== "none")
  );

  const steps = [
    { id: "pending", label: "Intake" },
    { id: "under_assessment", label: "Assessment" },
    { id: "approved", label: "Approval" },
    { id: "resolved", label: "Resolution" },
    { id: "closed", label: "Closed" },
  ];

  let activeStepIdx = 0;
  if (statusVal === "under_assessment") {
    activeStepIdx = 1;
  } else if (statusVal === "approved") {
    activeStepIdx = 2;
  } else if (statusVal === "in_progress" || statusVal === "resolved") {
    activeStepIdx = 3;
  } else if (statusVal === "closed") {
    activeStepIdx = 4;
  } else if (statusVal === "rejected") {
    activeStepIdx = -1;
  }

  // Documents list
  const documents = Array.isArray(claim.documents) ? claim.documents : [];
  const intakeDoc = documents.find((d) => getDocumentType(d) === "intake");
  const rejectionDoc = documents.find((d) => getDocumentType(d) === "rejection");
  const exchangeDoc = documents.find((d) => getDocumentType(d) === "exchange");
  const completionDoc = documents.find((d) => getDocumentType(d) === "completion");

  const handleOpenDocModal = (title, url, customFilename) => {
    const claimNum = claim?.claim_number || claim?.id || "";
    const filename =
      customFilename ||
      `${title.replace(/\s+/g, "_")}${claimNum ? `_${claimNum}` : ""}.pdf`;
    setSelectedDocForAction({
      title,
      url,
      filename,
    });
    setDocModalOpen(true);
  };

  return (
    <PageContainer className="space-y-6">
      {/* Header */}
      <PageHeader className="mb-0">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => navigate(all_routes.warrantyClaims)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <PageHeaderHeading
                title={`Claim #${claim.claim_number || claim.id}`}
              />
              <Badge
                variant={statusVariants[statusVal] || "outline"}
                className="text-xs uppercase font-medium"
              >
                {statusLabel}
              </Badge>
              {isNoWarranty && (
                <Badge variant="warning" className="text-xs">
                  No Warranty
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Filed on {formatDate(claim.created_at)} &bull; Store:{" "}
              {claim.store?.name || currentStore?.name || "Main Store"}
            </p>
          </div>
        </div>

        <PageHeaderActions>
          {/* Action buttons driven strictly by status.allowed_transitions */}
          {canAssess && (
            <Button onClick={() => setAssessModalOpen(true)}>
              <Clock className="mr-2 h-4 w-4" /> Assess Item
            </Button>
          )}

          {canApprove && (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setApproveModalOpen(true)}
            >
              <CheckCircle className="mr-2 h-4 w-4" /> Approve Claim
            </Button>
          )}

          {canFulfil && (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setFulfilModalOpen(true)}
            >
              <PackageCheck className="mr-2 h-4 w-4" /> Fulfil Replacement
            </Button>
          )}

          {canResolve && (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setResolveModalOpen(true)}
            >
              <CheckCircle className="mr-2 h-4 w-4" /> Resolve Claim
            </Button>
          )}

          {canClose && (
            <Button variant="outline" onClick={() => setCloseConfirmOpen(true)}>
              <CheckCircle className="mr-2 h-4 w-4" /> Close Claim
            </Button>
          )}

          {canReject && (
            <Button
              variant="outline"
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
              onClick={() => setRejectModalOpen(true)}
            >
              <XCircle className="mr-2 h-4 w-4" /> Reject
            </Button>
          )}

          {canSupplierClaim && (
            <Button variant="outline" onClick={() => setSupplierModalOpen(true)}>
              <Truck className="mr-2 h-4 w-4" /> Supplier RMA
            </Button>
          )}

          {canEscapeDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-destructive h-9 w-9"
              title="Delete / Cancel Claim"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      {/* Prominent Banners */}
      <div className="my-4">
        {/* New Claim Intake Success Banner */}
        {isNewClaimBanner && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 text-emerald-900 dark:text-emerald-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-full text-emerald-600 dark:text-emerald-400">
                <CheckCircle className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Warranty Claim #{claim.claim_number} Registered
                </h4>
                <p className="text-xs text-muted-foreground dark:text-emerald-300/80">
                  Intake document generated automatically. You can print the receipt or capture customer signature now.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {hasPermission("report warranty intake view") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs bg-background"
                  onClick={() =>
                    handleOpenDocModal(
                      "Warranty Intake Receipt",
                      warrantyApi.pdf.intake(claim.id),
                    )
                  }
                >
                  <Printer className="mr-1.5 h-3.5 w-3.5" />
                  Print / Download Intake Receipt
                </Button>
              )}
              {intakeDoc && !intakeDoc.is_signed && (
                <Button
                  size="sm"
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => {
                    setSelectedDocToSign(intakeDoc);
                    setSignModalOpen(true);
                  }}
                >
                  <PenTool className="mr-1.5 h-3.5 w-3.5" />
                  Capture Signature
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Awaiting Stock Banner */}
        {isAwaitingStock && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-amber-900 dark:text-amber-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
              <div>
                <h4 className="font-semibold text-sm">Awaiting Stock for Fulfilment</h4>
                <p className="text-xs text-muted-foreground dark:text-amber-300/80">
                  This claim was approved for replacement, but no serial unit was available in stock. Once replacement inventory arrives, issue it using Fulfil.
                </p>
              </div>
            </div>
            {canFulfil && (
              <Button
                size="sm"
                className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                onClick={() => setFulfilModalOpen(true)}
              >
                <PackageCheck className="mr-1.5 h-3.5 w-3.5" />
                Fulfil Replacement
              </Button>
            )}
          </div>
        )}

        {/* Overdue SLA Warnings */}
        {(claim.is_assessment_overdue || claim.is_resolution_overdue) && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3.5 text-xs text-destructive flex items-center gap-2.5 shadow-xs">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <strong>SLA Alert:</strong>{" "}
              {claim.is_assessment_overdue && claim.is_resolution_overdue
                ? "Assessment and resolution deadlines are overdue for this warranty claim."
                : claim.is_assessment_overdue
                  ? "Initial inspection deadline is overdue."
                  : "Resolution deadline is overdue."}
            </span>
          </div>
        )}

        {/* Expired / No Warranty Warning Banner */}
        {claim.is_warranty_valid === false && !isWarningDismissed && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-amber-950 dark:text-amber-200 flex items-start justify-between gap-3 shadow-xs">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg text-amber-700 dark:text-amber-400 shrink-0 mt-0.5">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-sm">
                    {isNoWarranty
                      ? "Item Sold Without Warranty Coverage"
                      : "Warranty Coverage Expired"}
                  </h4>
                  <Badge
                    variant="warning"
                    className="text-[10px] uppercase py-0 px-1.5 h-4 font-semibold"
                  >
                    {isNoWarranty ? "No Warranty" : "Expired"}
                  </Badge>
                </div>
                <p className="text-xs text-amber-900/80 dark:text-amber-200/80 leading-relaxed max-w-3xl">
                  {isNoWarranty
                    ? `This product (${claim.product?.name || claim.item_description}) was registered with No Warranty. Claim intake is accepted, but approving a free remedy (replacement, repair, or refund) will require manager authorization.`
                    : `The warranty coverage ended on ${formatDate(claim.warranty_end_date)}. Approving a free remedy (replacement, repair, or refund) will require manager authorization.`}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-amber-800 hover:text-amber-950 hover:bg-amber-500/20 shrink-0"
              onClick={() => setIsWarningDismissed(true)}
              title="Dismiss notice"
            >
              <XCircle className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Status Timeline Stepper */}
      <Card>
        <CardContent className="py-6 px-4 sm:px-8">
          <div className="max-w-3xl mx-auto flex items-start">
            {steps.map((step, idx) => {
              const isDone =
                (activeStepIdx > idx && activeStepIdx !== -1) ||
                statusVal === "closed";
              const isCurrent = activeStepIdx === idx;
              const isLineDone =
                (activeStepIdx > idx && activeStepIdx !== -1) ||
                statusVal === "closed";

              return (
                <React.Fragment key={step.id}>
                  {/* Step Node (Circle + Label) */}
                  <div className="flex flex-col items-center text-center shrink-0 w-16 sm:w-24">
                    <div
                      className={cn(
                        "h-9 w-9 sm:h-10 sm:w-10 rounded-full flex items-center justify-center font-semibold text-xs border-2 transition-all",
                        isDone
                          ? "bg-primary border-primary text-primary-foreground shadow-xs"
                          : isCurrent
                            ? "border-primary bg-background text-primary ring-4 ring-primary/15 font-bold shadow-xs"
                            : "border-muted-foreground/30 bg-muted/40 text-muted-foreground",
                      )}
                    >
                      {isDone ? (
                        <Check className="h-4 w-4 stroke-[2.5]" />
                      ) : (
                        idx + 1
                      )}
                    </div>
                    <span
                      className={cn(
                        "text-xs mt-2 transition-colors",
                        isCurrent
                          ? "font-semibold text-foreground"
                          : isDone
                            ? "font-medium text-foreground"
                            : "text-muted-foreground",
                      )}
                    >
                      {step.label}
                    </span>
                  </div>

                  {/* Connecting Line to next step only (never after the last step) */}
                  {idx < steps.length - 1 && (
                    <div
                      className={cn(
                        "flex-1 h-0.5 mt-[17px] sm:mt-[19px] mx-1 sm:mx-2 transition-colors duration-300",
                        isLineDone ? "bg-primary" : "bg-muted-foreground/20",
                      )}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left Column (2 Cols) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Claim & Product Overview */}
          <Card>
            <CardHeader className="py-3 px-5 border-b bg-muted/10 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Tag className="h-4 w-4 text-primary" />
                <span>Product &amp; Claim Details</span>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="capitalize text-xs font-normal">
                  Condition: {String(returnConditionLabel).replace(/_/g, " ")}
                </Badge>
                {isAwaitingStock && (
                  <Badge variant="warning" className="text-xs">
                    Awaiting Stock
                  </Badge>
                )}
                {canEditClaim && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs px-2.5"
                    onClick={() => setEditModalOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-5 space-y-5 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                <div>
                  <span className="text-xs text-muted-foreground block mb-0.5">
                    Product
                  </span>
                  <span className="font-semibold text-foreground block">
                    {claim.product?.name || claim.item_description || "N/A"}
                  </span>
                  {claim.product?.sku && (
                    <span className="text-xs text-muted-foreground font-mono block mt-0.5">
                      SKU: {claim.product.sku}
                    </span>
                  )}
                </div>

                <div>
                  <span className="text-xs text-muted-foreground block mb-0.5">
                    Item Serial Number
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-muted font-mono font-medium text-xs text-foreground">
                    {claim.serial_number || "Non-serialized"}
                  </span>
                </div>

                <div>
                  <span className="text-xs text-muted-foreground block mb-0.5">
                    Store Location
                  </span>
                  <span className="font-medium text-foreground block">
                    {claim.store?.name || currentStore?.name || "N/A"}
                  </span>
                </div>
              </div>

              <Separator />

              <div>
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                  Reported Defect Description
                </span>
                <div className="text-foreground leading-relaxed bg-muted/30 p-3.5 rounded-lg border text-sm">
                  {claim.defect_description || "No defect notes recorded."}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assessment Section */}
          {(claim.assessment_notes || claim.assessed_at || canAssess) && (
            <Card>
              <CardHeader className="py-3 px-5 border-b bg-muted/10 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>Hardware Assessment</span>
                </CardTitle>
                {claim.assessed_at ? (
                  <Badge variant="outline" className="text-xs">
                    Assessed on {formatDate(claim.assessed_at)}
                  </Badge>
                ) : (
                  <Badge variant="warning" className="text-xs">
                    Pending Inspection
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="p-5 text-sm space-y-3">
                {claim.assessment_notes ? (
                  <div className="space-y-2">
                    <p className="leading-relaxed bg-muted/30 p-3.5 rounded-lg border">
                      {claim.assessment_notes}
                    </p>
                    {assessedByName && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>Assessed by:</span>
                        <span className="font-medium text-foreground">
                          {assessedByName}
                        </span>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-muted-foreground py-1">
                    <p className="text-xs text-muted-foreground">
                      Physical inspection outcome has not been recorded yet. Diagnostic notes can be submitted using the Assess Item action.
                    </p>
                    {canAssess && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs shrink-0"
                        onClick={() => setAssessModalOpen(true)}
                      >
                        <Clock className="mr-1.5 h-3.5 w-3.5" />
                        Record Assessment
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Resolution / Remedy Card */}
          {claim.resolution_type && (
            <Card>
              <CardHeader className="py-3 px-5 border-b bg-emerald-500/5 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                  <CheckCircle className="h-4 w-4" />
                  <span>Approved Remedy &amp; Resolution</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-4 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground block">
                      Remedy Type
                    </span>
                    <span className="font-semibold text-foreground capitalize">
                      {resolutionTypeLabel || resolutionTypeVal}
                    </span>
                  </div>

                  {(claim.replacement_product?.name || claim.replacement_product) && (
                    <div>
                      <span className="text-xs text-muted-foreground block">
                        Replacement Product
                      </span>
                      <span className="font-medium text-foreground">
                        {claim.replacement_product?.name || claim.replacement_product}
                      </span>
                    </div>
                  )}

                  {(claim.replacement_serial?.serial_number || claim.replacement_serial_number) && (
                    <div>
                      <span className="text-xs text-muted-foreground block">
                        Replacement Serial Number
                      </span>
                      <span className="font-mono font-medium text-foreground">
                        {claim.replacement_serial?.serial_number || claim.replacement_serial_number}
                      </span>
                    </div>
                  )}

                  {(claim.replacement_sale?.invoice_number || claim.replacement_sale_id) && (
                    <div>
                      <span className="text-xs text-muted-foreground block">
                        Replacement Invoice
                      </span>
                      <span className="font-mono font-medium text-primary">
                        #{claim.replacement_sale?.invoice_number || claim.replacement_sale_id}
                      </span>
                    </div>
                  )}

                  {(claim.replacement_warranty_start_date || claim.replacement_warranty_end_date) && (
                    <div>
                      <span className="text-xs text-muted-foreground block">
                        Replacement Warranty
                      </span>
                      <span className="font-medium text-foreground text-xs">
                        {formatDate(claim.replacement_warranty_start_date)} – {formatDate(claim.replacement_warranty_end_date)}
                      </span>
                    </div>
                  )}

                  {claim.fulfilled_at && (
                    <div>
                      <span className="text-xs text-muted-foreground block">
                        Fulfilled On
                      </span>
                      <span className="font-medium text-foreground">
                        {formatDate(claim.fulfilled_at)}
                      </span>
                    </div>
                  )}

                  {claim.price_difference !== undefined &&
                    claim.price_difference !== null && (
                      <div>
                        <span className="text-xs text-muted-foreground block">
                          Price Difference
                        </span>
                        <span
                          className={`font-semibold ${parseFloat(claim.price_difference) > 0
                            ? "text-amber-600"
                            : parseFloat(claim.price_difference) < 0
                              ? "text-blue-600"
                              : "text-emerald-600"
                            }`}
                        >
                          {parseFloat(claim.price_difference) > 0
                            ? `+${currencySymbol}${parseFloat(claim.price_difference).toFixed(2)} (Amount Due)`
                            : parseFloat(claim.price_difference) < 0
                              ? `-${currencySymbol}${Math.abs(parseFloat(claim.price_difference)).toFixed(2)} (Credit Note)`
                              : `${currencySymbol}0.00 (Even)`}
                        </span>
                      </div>
                    )}
                </div>

                {claim.refund_justification && (
                  <div>
                    <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider block mb-1">
                      Refund Justification
                    </span>
                    <p className="bg-muted/30 p-2.5 rounded border text-xs">
                      {claim.refund_justification}
                    </p>
                  </div>
                )}

                {(claim.resolution_notes || claim.notes) && (
                  <div>
                    <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider block mb-1">
                      Resolution Notes
                    </span>
                    <p className="bg-muted/30 p-2.5 rounded border text-xs">
                      {claim.resolution_notes || claim.notes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Rejection Details (if rejected) */}
          {statusVal === "rejected" && (
            <Card className="border-destructive/30">
              <CardHeader className="py-3 px-5 border-b bg-destructive/5 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-destructive">
                  <XCircle className="h-4 w-4" />
                  <span>Claim Rejection</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-3 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground block">
                    Reason
                  </span>
                  <span className="font-semibold text-foreground capitalize">
                    {rejectionReasonLabel
                      ? String(rejectionReasonLabel).replace(/_/g, " ")
                      : "Other"}
                  </span>
                </div>
                {claim.rejection_notes && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">
                      Rejection Notes
                    </span>
                    <p className="bg-muted/30 p-2.5 rounded border text-xs">
                      {claim.rejection_notes}
                    </p>
                  </div>
                )}
                {rejectionDoc && hasPermission("report warranty rejection view") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handleOpenDocModal(
                        "Warranty Rejection Notice",
                        warrantyApi.pdf.rejection(claim.id),
                      )
                    }
                  >
                    <Printer className="mr-1.5 h-3.5 w-3.5" /> Print / Download
                    Rejection Notice
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Supplier Claim / RMA Recovery Panel (only when RMA exists) */}
          {hasSupplierRma && (
            <Card>
              <CardHeader className="py-3 px-5 border-b bg-muted/10 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Truck className="h-4 w-4 text-primary" />
                  <span>Supplier RMA Recovery</span>
                </CardTitle>
                {supplierClaimStatusLabel &&
                  supplierClaimStatusVal !== "none" && (
                    <Badge variant="outline" className="capitalize text-xs">
                      Status: {supplierClaimStatusLabel}
                    </Badge>
                  )}
              </CardHeader>
              <CardContent className="p-5 text-sm space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground block">
                      Supplier
                    </span>
                    <span className="font-medium text-foreground">
                      {claim.supplier?.name ||
                        claim.supplier?.supplier_name ||
                        "Vendor"}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block">
                      RMA Reference
                    </span>
                    <span className="font-mono font-medium text-foreground">
                      {claim.supplier_claim_reference || "Pending"}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block">
                      Expected Credit
                    </span>
                    <span className="font-semibold text-emerald-600">
                      {currencySymbol}
                      {parseFloat(claim.supplier_credit_amount || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column (1 Col) */}
        <div className="space-y-6">
          {/* Customer Card */}
          <Card>
            <CardHeader className="py-3 px-5 border-b bg-muted/10">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <User className="h-4 w-4 text-primary" />
                <span>Customer Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground text-xs">Name</span>
                <span className="font-medium text-foreground">
                  {claim.customer?.customer_name ||
                    claim.customer?.full_name ||
                    claim.customer?.name ||
                    "Walk-in Customer"}
                </span>
              </div>
              {claim.customer?.phone && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">Phone</span>
                  <span className="font-medium text-foreground">
                    {claim.customer.phone}
                  </span>
                </div>
              )}
              {claim.customer?.email && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">Email</span>
                  <span className="font-medium text-foreground truncate max-w-[180px]">
                    {claim.customer.email}
                  </span>
                </div>
              )}
              {claim.sale && (
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="text-muted-foreground text-xs">
                    Original Invoice
                  </span>
                  <span className="font-mono font-semibold text-primary text-xs bg-primary/10 px-2 py-0.5 rounded">
                    #{claim.sale.invoice_number || claim.sale.id}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Warranty Validity / Coverage Card */}
          <Card>
            <CardHeader className="py-3 px-5 border-b bg-muted/10">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span>Warranty Coverage</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground text-xs">Status</span>
                {isNoWarranty ? (
                  <Badge variant="warning">NO WARRANTY</Badge>
                ) : claim.is_warranty_valid ? (
                  <Badge variant="success">VALID</Badge>
                ) : (
                  <Badge variant="destructive">EXPIRED</Badge>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground text-xs">Start Date</span>
                <span className="font-medium">
                  {formatDate(claim.warranty_start_date)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground text-xs">End Date</span>
                <span className="font-medium">
                  {formatDate(claim.warranty_end_date)}
                </span>
              </div>
              {claim.days_since_created !== undefined &&
                claim.days_since_created !== null && (
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-muted-foreground text-xs">
                      Claim Age
                    </span>
                    <span className="font-medium">
                      {Math.max(0, Math.floor(claim.days_since_created)) === 0
                        ? "Today (< 1 day)"
                        : `${Math.max(0, Math.floor(claim.days_since_created))} days`}
                    </span>
                  </div>
                )}
            </CardContent>
          </Card>

          {/* Documents & Signature Panel (Section 4) */}
          <Card>
            <CardHeader className="py-3 px-5 border-b bg-muted/10">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <span>Documents &amp; Signatures</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3.5">
              {/* Intake Document */}
              <div className="p-3.5 rounded-lg border bg-card shadow-2xs space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-md bg-primary/10 text-primary shrink-0">
                      <Receipt className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="font-semibold text-xs text-foreground block">
                        Intake Receipt
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        Issued at claim registration
                      </span>
                    </div>
                  </div>
                  {intakeDoc?.is_signed ? (
                    <Badge variant="success" className="text-[10px] h-5 px-2 gap-1 font-medium">
                      <CheckCircle className="h-3 w-3" /> Signed
                    </Badge>
                  ) : intakeDoc ? (
                    <Badge
                      variant="outline"
                      className="text-[10px] h-5 px-2 text-amber-600 dark:text-amber-400 border-amber-500/40 bg-amber-500/10 font-medium"
                    >
                      Unsigned
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      className="text-[10px] h-5 px-2 text-muted-foreground font-medium"
                    >
                      Not Issued
                    </Badge>
                  )}
                </div>

                {intakeDoc?.is_signed && (intakeDoc.signed_by_name || intakeDoc.signed_at) && (
                  <div className="text-[11px] text-muted-foreground flex items-center justify-between bg-muted/40 px-2.5 py-1.5 rounded border border-border/50">
                    <span>By <strong className="text-foreground">{intakeDoc.signed_by_name || "Customer"}</strong></span>
                    {intakeDoc.signed_at && <span>{formatDate(intakeDoc.signed_at)}</span>}
                  </div>
                )}

                <div className="pt-0.5">
                  {intakeDoc ? (
                    !intakeDoc.is_signed ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {hasPermission("report warranty intake view") && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs font-medium w-full bg-background hover:bg-muted border-input shadow-2xs gap-1.5"
                            onClick={() =>
                              handleOpenDocModal(
                                "Warranty Intake Receipt",
                                warrantyApi.pdf.intake(claim.id),
                              )
                            }
                          >
                            <Printer className="h-3.5 w-3.5 text-primary" />
                            <span>Print / Download</span>
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 text-xs font-semibold w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs gap-1.5"
                          onClick={() => {
                            setSelectedDocToSign(intakeDoc);
                            setSignModalOpen(true);
                          }}
                        >
                          <PenTool className="h-3.5 w-3.5" />
                          <span>Sign Document</span>
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        {hasPermission("report warranty intake view") && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs font-medium flex-1 min-w-[130px] bg-background hover:bg-muted border-input shadow-2xs gap-1.5"
                            onClick={() =>
                              handleOpenDocModal(
                                "Warranty Intake Receipt",
                                warrantyApi.pdf.intake(claim.id),
                              )
                            }
                          >
                            <Printer className="h-3.5 w-3.5 text-primary" />
                            <span>Print / Download</span>
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted border-input shadow-2xs gap-1.5"
                          onClick={() => {
                            setSelectedDocToSign(intakeDoc);
                            setSignModalOpen(true);
                          }}
                        >
                          <PenTool className="h-3.5 w-3.5" />
                          <span>Re-sign</span>
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs font-medium text-destructive hover:text-destructive hover:bg-destructive/10 border-input shadow-2xs gap-1.5"
                          onClick={() => setConfirmRemoveDoc({ doc: intakeDoc, type: "intake" })}
                          disabled={removeSigMutation.isPending && removingDocId === intakeDoc.id}
                          title="Remove digital signature for physical signing"
                        >
                          {removeSigMutation.isPending && removingDocId === intakeDoc.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RotateCcw className="h-3.5 w-3.5" />
                          )}
                          <span>Remove Signature</span>
                        </Button>
                      </div>
                    )
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs font-medium w-full gap-1.5"
                      onClick={() => issueDocMutation.mutate("intake")}
                      disabled={issueDocMutation.isPending}
                    >
                      {issueDocMutation.isPending ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          <span>Issuing Receipt...</span>
                        </>
                      ) : (
                        <>
                          <FilePlus className="h-3.5 w-3.5" />
                          <span>Issue Intake Receipt</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Exchange Document (for replacements) */}
              {(resolutionTypeVal === "replacement" || exchangeDoc) && (
                <div className="p-3.5 rounded-lg border bg-card shadow-2xs space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="p-1.5 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 shrink-0">
                        <PackageCheck className="h-4 w-4" />
                      </div>
                      <div>
                        <span className="font-semibold text-xs text-foreground block">
                          Exchange Slip
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          Replacement item voucher
                        </span>
                      </div>
                    </div>
                    {exchangeDoc?.is_signed ? (
                      <Badge variant="success" className="text-[10px] h-5 px-2 gap-1 font-medium">
                        <CheckCircle className="h-3 w-3" /> Signed
                      </Badge>
                    ) : exchangeDoc ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5 px-2 text-amber-600 dark:text-amber-400 border-amber-500/40 bg-amber-500/10 font-medium"
                      >
                        Unsigned
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="text-[10px] h-5 px-2 text-muted-foreground font-medium"
                      >
                        Not Issued
                      </Badge>
                    )}
                  </div>

                  {exchangeDoc?.is_signed && (exchangeDoc.signed_by_name || exchangeDoc.signed_at) && (
                    <div className="text-[11px] text-muted-foreground flex items-center justify-between bg-muted/40 px-2.5 py-1.5 rounded border border-border/50">
                      <span>By <strong className="text-foreground">{exchangeDoc.signed_by_name || "Customer"}</strong></span>
                      {exchangeDoc.signed_at && <span>{formatDate(exchangeDoc.signed_at)}</span>}
                    </div>
                  )}

                  <div className="pt-0.5">
                    {exchangeDoc ? (
                      !exchangeDoc.is_signed ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {hasPermission("report warranty exchange view") && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs font-medium w-full bg-background hover:bg-muted border-input shadow-2xs gap-1.5"
                              onClick={() =>
                                handleOpenDocModal(
                                  "Warranty Exchange Slip",
                                  warrantyApi.pdf.exchange(claim.id),
                                )
                              }
                            >
                              <Printer className="h-3.5 w-3.5 text-blue-600" />
                              <span>Print / Download</span>
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            className="h-8 text-xs font-semibold w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs gap-1.5"
                            onClick={() => {
                              setSelectedDocToSign(exchangeDoc);
                              setSignModalOpen(true);
                            }}
                          >
                            <PenTool className="h-3.5 w-3.5" />
                            <span>Sign Document</span>
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          {hasPermission("report warranty exchange view") && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs font-medium flex-1 min-w-[130px] bg-background hover:bg-muted border-input shadow-2xs gap-1.5"
                              onClick={() =>
                                handleOpenDocModal(
                                  "Warranty Exchange Slip",
                                  warrantyApi.pdf.exchange(claim.id),
                                )
                              }
                            >
                              <Printer className="h-3.5 w-3.5 text-blue-600" />
                              <span>Print / Download</span>
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted border-input shadow-2xs gap-1.5"
                            onClick={() => {
                              setSelectedDocToSign(exchangeDoc);
                              setSignModalOpen(true);
                            }}
                          >
                            <PenTool className="h-3.5 w-3.5" />
                            <span>Re-sign</span>
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs font-medium text-destructive hover:text-destructive hover:bg-destructive/10 border-input shadow-2xs gap-1.5"
                            onClick={() => setConfirmRemoveDoc({ doc: exchangeDoc, type: "exchange" })}
                            disabled={removeSigMutation.isPending && removingDocId === exchangeDoc.id}
                            title="Remove digital signature for physical signing"
                          >
                            {removeSigMutation.isPending && removingDocId === exchangeDoc.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3.5 w-3.5" />
                            )}
                            <span>Remove Signature</span>
                          </Button>
                        </div>
                      )
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs font-medium w-full gap-1.5"
                        onClick={() => issueDocMutation.mutate("exchange")}
                        disabled={issueDocMutation.isPending}
                      >
                        {issueDocMutation.isPending ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            <span>Issuing Slip...</span>
                          </>
                        ) : (
                          <>
                            <FilePlus className="h-3.5 w-3.5" />
                            <span>Issue Exchange Slip</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Completion Document (when resolved/closed) */}
              {(["resolved", "closed"].includes(statusVal) || completionDoc) && (
                <div className="p-3.5 rounded-lg border bg-card shadow-2xs space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="p-1.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
                        <CheckCircle className="h-4 w-4" />
                      </div>
                      <div>
                        <span className="font-semibold text-xs text-foreground block">
                          Completion Certificate
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          Resolution handover notice
                        </span>
                      </div>
                    </div>
                    {completionDoc?.is_signed ? (
                      <Badge variant="success" className="text-[10px] h-5 px-2 gap-1 font-medium">
                        <CheckCircle className="h-3 w-3" /> Signed
                      </Badge>
                    ) : completionDoc ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5 px-2 text-amber-600 dark:text-amber-400 border-amber-500/40 bg-amber-500/10 font-medium"
                      >
                        Unsigned
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="text-[10px] h-5 px-2 text-muted-foreground font-medium"
                      >
                        Not Issued
                      </Badge>
                    )}
                  </div>

                  {completionDoc?.is_signed && (completionDoc.signed_by_name || completionDoc.signed_at) && (
                    <div className="text-[11px] text-muted-foreground flex items-center justify-between bg-muted/40 px-2.5 py-1.5 rounded border border-border/50">
                      <span>By <strong className="text-foreground">{completionDoc.signed_by_name || "Customer"}</strong></span>
                      {completionDoc.signed_at && <span>{formatDate(completionDoc.signed_at)}</span>}
                    </div>
                  )}

                  <div className="pt-0.5">
                    {completionDoc ? (
                      !completionDoc.is_signed ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {hasPermission("report warranty completion view") && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs font-medium w-full bg-background hover:bg-muted border-input shadow-2xs gap-1.5"
                              onClick={() =>
                                handleOpenDocModal(
                                  "Warranty Completion Certificate",
                                  warrantyApi.pdf.completion(claim.id),
                                )
                              }
                            >
                              <Printer className="h-3.5 w-3.5 text-emerald-600" />
                              <span>Print / Download</span>
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            className="h-8 text-xs font-semibold w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs gap-1.5"
                            onClick={() => {
                              setSelectedDocToSign(completionDoc);
                              setSignModalOpen(true);
                            }}
                          >
                            <PenTool className="h-3.5 w-3.5" />
                            <span>Sign Document</span>
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          {hasPermission("report warranty completion view") && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs font-medium flex-1 min-w-[130px] bg-background hover:bg-muted border-input shadow-2xs gap-1.5"
                              onClick={() =>
                                handleOpenDocModal(
                                  "Warranty Completion Certificate",
                                  warrantyApi.pdf.completion(claim.id),
                                )
                              }
                            >
                              <Printer className="h-3.5 w-3.5 text-emerald-600" />
                              <span>Print / Download</span>
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted border-input shadow-2xs gap-1.5"
                            onClick={() => {
                              setSelectedDocToSign(completionDoc);
                              setSignModalOpen(true);
                            }}
                          >
                            <PenTool className="h-3.5 w-3.5" />
                            <span>Re-sign</span>
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs font-medium text-destructive hover:text-destructive hover:bg-destructive/10 border-input shadow-2xs gap-1.5"
                            onClick={() => setConfirmRemoveDoc({ doc: completionDoc, type: "completion" })}
                            disabled={removeSigMutation.isPending && removingDocId === completionDoc.id}
                            title="Remove digital signature for physical signing"
                          >
                            {removeSigMutation.isPending && removingDocId === completionDoc.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3.5 w-3.5" />
                            )}
                            <span>Remove Signature</span>
                          </Button>
                        </div>
                      )
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs font-medium w-full gap-1.5"
                        onClick={() => issueDocMutation.mutate("completion")}
                        disabled={issueDocMutation.isPending}
                      >
                        {issueDocMutation.isPending ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            <span>Issuing Certificate...</span>
                          </>
                        ) : (
                          <>
                            <FilePlus className="h-3.5 w-3.5" />
                            <span>Issue Completion Certificate</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Rejection Notice (when rejected or rejectionDoc exists) */}
              {(statusVal === "rejected" || rejectionDoc) && (
                <div className="p-3.5 rounded-lg border bg-card shadow-2xs space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="p-1.5 rounded-md bg-destructive/10 text-destructive shrink-0">
                        <XCircle className="h-4 w-4" />
                      </div>
                      <div>
                        <span className="font-semibold text-xs text-foreground block">
                          Rejection Notice
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          Customer formal notice
                        </span>
                      </div>
                    </div>
                    {rejectionDoc ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5 px-2 text-destructive border-destructive/30 bg-destructive/5 font-medium"
                      >
                        Issued
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="text-[10px] h-5 px-2 text-muted-foreground font-medium"
                      >
                        Not Issued
                      </Badge>
                    )}
                  </div>

                  <div className="pt-0.5">
                    {rejectionDoc ? (
                      hasPermission("report warranty rejection view") && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs font-medium w-full bg-background hover:bg-muted border-input shadow-2xs gap-1.5"
                          onClick={() =>
                            handleOpenDocModal(
                              "Warranty Rejection Notice",
                              warrantyApi.pdf.rejection(claim.id),
                            )
                          }
                        >
                          <Printer className="h-3.5 w-3.5 text-destructive" />
                          <span>Print / Download Notice</span>
                        </Button>
                      )
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 text-xs font-medium w-full bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/30 shadow-2xs gap-1.5"
                        onClick={() => issueDocMutation.mutate("rejection")}
                        disabled={issueDocMutation.isPending}
                      >
                        {issueDocMutation.isPending ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            <span>Issuing Notice...</span>
                          </>
                        ) : (
                          <>
                            <FilePlus className="h-3.5 w-3.5" />
                            <span>Issue Rejection Notice</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Credit Note Document (if credit note exists) */}
              {(claim.credit_note_id || claim.credit_note) && (
                <div className="p-3.5 rounded-lg border bg-blue-500/10 border-blue-500/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="p-1.5 rounded-md bg-blue-500/20 text-blue-700 dark:text-blue-300 shrink-0">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div>
                        <span className="font-semibold text-xs text-blue-900 dark:text-blue-300 block">
                          Credit Note
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          Refund / downgrade adjustment
                        </span>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px] h-5 px-2 bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30 font-medium">
                      Issued
                    </Badge>
                  </div>

                  <div className="pt-0.5">
                    {hasPermission("report credit note view") && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs font-medium w-full bg-background hover:bg-muted border-input shadow-2xs gap-1.5 text-blue-800 dark:text-blue-200"
                        onClick={() =>
                          handleOpenDocModal(
                            "Credit Note",
                            warrantyApi.pdf.creditNote(
                              claim.credit_note_id || claim.credit_note?.id,
                            ),
                          )
                        }
                      >
                        <Printer className="h-3.5 w-3.5 text-blue-600" />
                        <span>Print / Download Credit Note</span>
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Action Modals */}
      <AssessClaimModal
        open={assessModalOpen}
        onOpenChange={setAssessModalOpen}
        claim={claim}
        onDone={() => refetch()}
      />

      <ApproveClaimModal
        open={approveModalOpen}
        onOpenChange={setApproveModalOpen}
        claim={claim}
        onDone={() => refetch()}
      />

      <FulfilReplacementModal
        open={fulfilModalOpen}
        onOpenChange={setFulfilModalOpen}
        claim={claim}
        onDone={() => refetch()}
      />

      <RejectClaimModal
        open={rejectModalOpen}
        onOpenChange={setRejectModalOpen}
        claim={claim}
        onDone={() => refetch()}
      />

      <ResolveClaimModal
        open={resolveModalOpen}
        onOpenChange={setResolveModalOpen}
        claim={claim}
        onDone={() => refetch()}
      />

      <SupplierRmaModal
        open={supplierModalOpen}
        onOpenChange={setSupplierModalOpen}
        claim={claim}
        onDone={() => refetch()}
      />

      <SignatureCaptureModal
        open={signModalOpen}
        onOpenChange={setSignModalOpen}
        claim={claim}
        documentObj={selectedDocToSign}
        onDone={() => refetch()}
      />

      <WarrantyDocumentModal
        open={docModalOpen}
        onOpenChange={setDocModalOpen}
        documentTitle={selectedDocForAction.title}
        claimNumber={claim.claim_number || claim.id}
        url={selectedDocForAction.url}
        filename={selectedDocForAction.filename}
      />

      {/* Close Confirm Dialog */}
      <AlertDialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close Warranty Claim?</AlertDialogTitle>
            <AlertDialogDescription>
              Mark claim #{claim.claim_number} as closed. This action is terminal and will finalize all claim records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => closeMutation.mutate()}
              disabled={closeMutation.isPending}
            >
              {closeMutation.isPending ? "Closing..." : "Close Claim"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete / Escape Hatch Confirm Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject and delete claim?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently reject claim #{claim.claim_number} via the escape hatch. The claim serial will be restored to sold status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Processing..." : "Confirm Reject / Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Remove Signature Confirm Dialog */}
      <AlertDialog
        open={Boolean(confirmRemoveDoc)}
        onOpenChange={(open) => !open && setConfirmRemoveDoc(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Digital Signature?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the digital signature from this document so that it can be printed with a blank line for physical signing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeSigMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removeSigMutation.isPending}
              onClick={() => {
                if (confirmRemoveDoc) {
                  removeSigMutation.mutate({
                    documentId: confirmRemoveDoc.doc.id,
                    type: confirmRemoveDoc.type,
                  });
                }
              }}
            >
              {removeSigMutation.isPending ? "Removing..." : "Remove Signature"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Warranty Claim Modal */}
      <EditWarrantyClaimModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        claim={claim}
        onDone={refetch}
      />
    </PageContainer>
  );
};

export default WarrantyClaimDetail;
